// Constraint entries of the zinc-puzzle document, as in docs/CONSTRAINTS.md. The core reads the
// same JSON (core/src/plugins); the web app draws from it.
import type { Rc } from './puzzle';

/** Drawing overrides on any entry; never logic. */
export interface ConstraintStyle {
  color?: string;
  width?: number;
  [key: string]: unknown;
}

interface Base {
  style?: ConstraintStyle;
  label?: string;
  /** Round 7c: whether the rule reads the cells' values, which a value rule (negative digits,
   * contract killers, liars) makes differ from their digits. Killer, arrow, edge-sum, ratio,
   * difference, whispers and zipper read values in such a puzzle unless this is false. */
  values?: boolean;
}

interface CellsEntry extends Base {
  cells: Rc[];
}

export type ExtraRegion = CellsEntry & { type: 'extra-region' };
export type Diagonal = Base & { type: 'diagonal'; direction: 'positive' | 'negative' };
export type DisjointGroups = Base & { type: 'disjoint-groups' };

export type Odd = CellsEntry & { type: 'odd' };
export type Even = CellsEntry & { type: 'even' };
export type Minimum = CellsEntry & { type: 'minimum' };
export type Maximum = CellsEntry & { type: 'maximum' };
export type Fortress = CellsEntry & { type: 'fortress' };

export const LINE_TYPES = [
  'thermo',
  'slow-thermo',
  'double-arrow',
  'renban',
  'nabner',
  'whispers',
  'palindrome',
  'region-sum',
  'between',
  'lockout',
  'entropic',
  'modular',
  'parity',
  'zipper',
  'kropki-line',
  'sequence',
  'bulbless-thermo',
  'pointing-thermo',
] as const;
export type LineType = (typeof LINE_TYPES)[number];
export type Line = CellsEntry & {
  type: LineType;
  /** whispers (default 5), lockout (default 4). */
  difference?: number;
  /** modular (default 3). */
  modulus?: number;
  /** renban: lines with the same group are one set ("each cross is a renban"). */
  group?: string;
};
export type Arrow = CellsEntry & { type: 'arrow'; circle: Rc[] };

export type Killer = CellsEntry & {
  type: 'killer';
  sum?: number | null;
  repeats?: boolean;
  /** Digits that share an edge within the cage aren't consecutive. */
  nonConsecutive?: boolean;
  /** Each digit in the cage appears as many times as its value (repeats allowed). */
  counting?: boolean;
  /** Possible totals when the rules give a choice ("sum to either 1 or 12"). */
  sums?: number[];
};
export type LittleKiller = Base & {
  type: 'little-killer';
  clue: Rc;
  direction: [number, number];
  sum: number;
};
export const OUTSIDE_TYPES = [
  'sandwich',
  'x-sum',
  'skyscraper',
  'frame-sum',
  'battlefield',
  'numbered-room',
  'outside-digits',
  'increasing-run',
  'up-to-sum',
] as const;
export type OutsideType = (typeof OUTSIDE_TYPES)[number];
export type OutsideClue = Base & {
  type: OutsideType;
  clue: Rc;
  /** sandwich, x-sum, frame-sum, battlefield. */
  sum?: number;
  /** skyscraper. */
  count?: number;
  /** numbered-room: the digit that sits in the Nth cell; increasing-run: the longest run. */
  value?: number;
  /** outside-digits: the digits that appear among the first `count` cells (default 3). */
  digits?: (string | number)[];
  /** frame-sum: cells counted (default: the box size in that direction). */
  length?: number;
};
export type Quadruple = CellsEntry & { type: 'quadruple'; digits: string[] };

export const EDGE_TYPES = [
  'difference',
  'ratio',
  'edge-sum',
  'greater-than',
  'kropki-either',
] as const;
export type EdgeType = (typeof EDGE_TYPES)[number];
export type EdgeClue = CellsEntry & { type: EdgeType; value?: number; negative?: false };
/** "All dots given": every adjacent pair without a clue of the family avoids `values`. */
export type NegativeEdge = Base & {
  type: 'difference' | 'ratio' | 'edge-sum';
  negative: true;
  values: number[];
};

export type AntiKnight = Base & { type: 'anti-knight' };
export type AntiKing = Base & { type: 'anti-king' };
export type NonConsecutive = Base & { type: 'non-consecutive' };
export type Clone = CellsEntry & { type: 'clone'; copy: Rc[] };
export type MagicSquare = CellsEntry & {
  type: 'magic-square';
  /** false: only the rows and columns share the sum ("within every box, ..."). Default true. */
  diagonals?: boolean;
};
/** Chess pieces on digits and coloured cages: rules in words, not drawn. */
export type Queen = Base & { type: 'queen'; digits: (string | number)[] };
export type KnightDistinct = Base & { type: 'knight-distinct'; digits: (string | number)[] };
export type ChessKings = Base & {
  type: 'chess-kings';
  /** How many digits are kings (found while solving); default 2. */
  count?: number;
  /** The king digits, when the rules name them. */
  digits?: (string | number)[];
};
export type CageColours = Base & { type: 'cage-colours'; colours?: number };
/** Pack 8 (BremSter's fog playlist, 2026-11-01). */
export type Friendly = CellsEntry & { type: 'friendly' };
export type BoxIndexer = CellsEntry & { type: 'box-indexer' };
export type Diversity = CellsEntry & { type: 'diversity' };
/** The four cells around a corner, clockwise from the top left, and the wheel's digits in the
 * same order. */
export type Wheel = CellsEntry & { type: 'wheel'; digits: string[] };
export type AntiDiagonal = Base & {
  type: 'anti-diagonal';
  direction: 'positive' | 'negative';
  /** How many different digits the diagonal may hold (default 3). */
  count?: number;
};
/** One of several rules holds; the solver finds which. */
export type Either = Base & { type: 'either'; options: ConstraintEntry[] };

/** Pack 6: shading. The solver finds a shading alongside the digits (each cell shaded or
 * not); rules about one colour speak of the unshaded cells with `unshaded: true`. Only the
 * circles of `view-count` and `area-clues` are drawn; the rest is in words. */
interface ShadingBase extends Base {
  unshaded?: boolean;
}
export type Shaded = ShadingBase & { type: 'shaded'; cells: Rc[] };
export type Connected = ShadingBase & { type: 'connected' };
export type NoSquare = ShadingBase & { type: 'no-2x2' };
export type AreaSize = ShadingBase & { type: 'area-size'; size: number };
export type ReachEdge = ShadingBase & { type: 'reach-edge' };
export type ShadeCount = ShadingBase & { type: 'shade-count'; cells: Rc[]; count: number };
/** The cell is unshaded; its digit (or `value`) counts the unshaded cells it sees. */
export type ViewCount = Base & { type: 'view-count'; cell: Rc; value?: number };
export type ShadedFortress = Base & { type: 'shaded-fortress' };
export type ShadedRatio = Base & { type: 'shaded-ratio'; value?: number };
export type AreaClues = ShadingBase & { type: 'area-clues'; cells: Rc[] };
export type AreaDigits = ShadingBase & { type: 'area-digits' };
/** `ends` may sit just outside the grid: the snake comes in from there. */
export type Snake = Base & { type: 'snake'; length?: number; ends?: [Rc, Rc] };
export type SnakeTurns = Base & { type: 'snake-turns'; turn?: 'even' | 'odd' };
export type ShadedWhispers = Base & { type: 'shaded-whispers'; difference?: number };
/** A cage whose `sum` counts only its unshaded cells; drawn like a killer cage. */
export type UnshadedSum = Base & { type: 'unshaded-sum'; cells: Rc[]; sum: number };
/** The left and right edges join with a half twist (for the shading rules). */
export type Mobius = Base & { type: 'mobius' };

/** Pack 9 (the needs-a-plugin list, 2026-11-02). */
export type ProductLine = CellsEntry & { type: 'product-line'; product: number };
/** The listed digits appear among the cells: a rule in words, not drawn. */
export type Contains = CellsEntry & { type: 'contains'; digits: (string | number)[] };
export type SameParity = CellsEntry & { type: 'same-parity' };
export type CountingCircles = CellsEntry & { type: 'counting-circles' };
export type NeverBoth = Base & { type: 'never-both'; parity?: 'even' | 'odd' };
export type NoConsecutiveTriple = Base & { type: 'no-consecutive-triple'; diagonals?: boolean };
export type DominantLine = CellsEntry & { type: 'dominant-line' };
/** A marked 2×2 (its four cells), or `negative`: every checkerboard is marked. */
export type Battenburg = Base & { type: 'battenburg'; cells?: Rc[]; negative?: boolean };
export type SnakeSequence = Base & { type: 'snake-sequence'; digits: string | (string | number)[] };

/** Round 7c: values that differ from the digits. Rules in words, not drawn. */
export type NegativeDigits = Base & {
  type: 'negative-digits';
  /** Each of these cages holds exactly one negative digit, and none are outside them. */
  cages?: Rc[][];
  /** The same for every killer cage. */
  killerCages?: boolean;
  /** Every row, column and box holds exactly one negative digit. */
  perHouse?: boolean;
  /** No two negative digits share a row, column or box. */
  apart?: boolean;
  /** No two negative digits have the same value. */
  distinct?: boolean;
};
/** Every killer cage (or each of `cages`) assassinates the cell its two non-zero values name. */
export type ContractKiller = Base & { type: 'contract-killer'; cages?: Rc[][] };
export type Liars = Base & {
  type: 'liars';
  perHouse: true;
  distinctDigits?: boolean;
  distinctValues?: boolean;
};
/** The killer cages the line runs through may lie: exactly `count` of them do, or as many as
 * the digit in `countCell`. Drawn as a line. */
export type LiarLine = CellsEntry & { type: 'liar-line'; count?: number; countCell?: Rc };

export const SHADING_TYPES = [
  'shaded',
  'connected',
  'no-2x2',
  'area-size',
  'reach-edge',
  'shade-count',
  'view-count',
  'shaded-fortress',
  'shaded-ratio',
  'area-clues',
  'area-digits',
  'snake',
  'snake-sequence',
  'snake-turns',
  'shaded-whispers',
  'unshaded-sum',
  'mobius',
] as const;

/** Every constraint type with a plugin (Packs 0–5). */
export type KnownConstraint =
  | ExtraRegion
  | Diagonal
  | DisjointGroups
  | Odd
  | Even
  | Minimum
  | Maximum
  | Fortress
  | Line
  | Arrow
  | Killer
  | LittleKiller
  | OutsideClue
  | Quadruple
  | EdgeClue
  | NegativeEdge
  | AntiKnight
  | AntiKing
  | NonConsecutive
  | Clone
  | MagicSquare
  | Queen
  | KnightDistinct
  | ChessKings
  | CageColours
  | Friendly
  | BoxIndexer
  | Diversity
  | Wheel
  | AntiDiagonal
  | Either
  | Shaded
  | Connected
  | NoSquare
  | AreaSize
  | ReachEdge
  | ShadeCount
  | ViewCount
  | ShadedFortress
  | ShadedRatio
  | AreaClues
  | AreaDigits
  | Snake
  | SnakeSequence
  | SnakeTurns
  | ShadedWhispers
  | UnshadedSum
  | Mobius
  | ProductLine
  | Contains
  | SameParity
  | CountingCircles
  | NeverBoth
  | NoConsecutiveTriple
  | DominantLine
  | Battenburg
  | NegativeDigits
  | ContractKiller
  | Liars
  | LiarLine;

export type KnownType = KnownConstraint['type'];

export const KNOWN_TYPES: readonly KnownType[] = [
  'extra-region',
  'diagonal',
  'disjoint-groups',
  'odd',
  'even',
  'minimum',
  'maximum',
  'fortress',
  ...LINE_TYPES,
  'arrow',
  'killer',
  'little-killer',
  ...OUTSIDE_TYPES,
  'quadruple',
  ...EDGE_TYPES,
  'anti-knight',
  'anti-king',
  'non-consecutive',
  'clone',
  'magic-square',
  'queen',
  'knight-distinct',
  'chess-kings',
  'cage-colours',
  'friendly',
  'box-indexer',
  'wheel',
  'diversity',
  'anti-diagonal',
  'either',
  ...SHADING_TYPES,
  'product-line',
  'contains',
  'same-parity',
  'counting-circles',
  'never-both',
  'no-consecutive-triple',
  'dominant-line',
  'battenburg',
  'negative-digits',
  'contract-killer',
  'liars',
  'liar-line',
];

/** A document entry: a known constraint or one the core doesn't know yet. */
export type ConstraintEntry = { type: string } & Record<string, unknown>;

export function isKnown(entry: ConstraintEntry): entry is ConstraintEntry & KnownConstraint {
  return (KNOWN_TYPES as readonly string[]).includes(entry.type);
}

/** Names for tags, rules text and the library. */
export const CONSTRAINT_NAMES: Record<KnownType, string> = {
  'extra-region': 'Extra regions',
  diagonal: 'Diagonals',
  'disjoint-groups': 'Disjoint groups',
  odd: 'Odd cells',
  even: 'Even cells',
  minimum: 'Minimum cells',
  maximum: 'Maximum cells',
  fortress: 'Fortress',
  thermo: 'Thermo',
  'slow-thermo': 'Slow thermo',
  arrow: 'Arrow',
  'double-arrow': 'Double arrow',
  renban: 'Renban',
  nabner: 'Nabner',
  whispers: 'Whispers',
  palindrome: 'Palindrome',
  'region-sum': 'Region sum',
  between: 'Between lines',
  lockout: 'Lockout',
  entropic: 'Entropic',
  modular: 'Modular',
  parity: 'Parity lines',
  zipper: 'Zipper',
  killer: 'Killer',
  'little-killer': 'Little killer',
  sandwich: 'Sandwich',
  'x-sum': 'X-sums',
  skyscraper: 'Skyscrapers',
  'frame-sum': 'Frame sums',
  battlefield: 'Battlefield',
  quadruple: 'Quadruples',
  difference: 'Kropki',
  ratio: 'Kropki',
  'edge-sum': 'XV',
  'greater-than': 'Greater than',
  'anti-knight': 'Anti-knight',
  'anti-king': 'Anti-king',
  'non-consecutive': 'Non-consecutive',
  clone: 'Clones',
  'magic-square': 'Magic square',
  queen: 'Queens',
  'knight-distinct': 'Knights on digits',
  'chess-kings': 'Chess kings',
  'cage-colours': 'Coloured cages',
  'kropki-line': 'Kropki lines',
  'kropki-either': 'Grey dots',
  sequence: 'Sequence lines',
  'bulbless-thermo': 'Bulbless thermo',
  'pointing-thermo': 'Pointing thermo',
  'numbered-room': 'Numbered rooms',
  'outside-digits': 'Outside digits',
  'increasing-run': 'Increasing runs',
  'up-to-sum': 'Up-to-N sums',
  friendly: 'Friendly cells',
  'box-indexer': 'Box indexing',
  wheel: 'Wheels',
  diversity: 'Diversity circles',
  'anti-diagonal': 'Anti-diagonal',
  either: 'Either rule',
  shaded: 'Shading',
  connected: 'Shading',
  'no-2x2': 'Shading',
  'area-size': 'Shading',
  'reach-edge': 'Shading',
  'shade-count': 'Shading',
  'view-count': 'View counts',
  'shaded-fortress': 'Shading',
  'shaded-ratio': 'Shading',
  'area-clues': 'Shading',
  'area-digits': 'Shading',
  snake: 'Snake',
  'snake-sequence': 'Snake',
  'snake-turns': 'Snake',
  'shaded-whispers': 'Snake',
  'unshaded-sum': 'Killer',
  mobius: 'Möbius strip',
  'product-line': 'Product lines',
  contains: 'Listed digits',
  'same-parity': 'Same parity',
  'counting-circles': 'Counting circles',
  'never-both': 'Never both even',
  'no-consecutive-triple': 'No three consecutive',
  'dominant-line': 'Dominant lines',
  battenburg: 'Battenburg',
  'negative-digits': 'Negative digits',
  'contract-killer': 'Contract killers',
  liars: 'Liars',
  'liar-line': 'Liar lines',
};
